import { Component, NgZone } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { LineLogin } from '@awesome-cordova-plugins/line-login/ngx';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { NavController, Platform, ToastController } from '@ionic/angular';
import { environment } from '@mommy/environments/environment';
import { MemberActionLogService } from '@mommy/services/member/member-action-log.service';
import { StateUtilsService } from '@mommy/services/state-utils.service';
import { StorageService } from '@mommy/services/storage.service';
import { UtilService } from '@mommy/services/util.service';
import { AuthState } from '@mommy/state/auth/auth.state';
import { UserState } from '@mommy/state/user/user.state';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import * as fb from 'firebase/compat/app';
import * as _ from 'lodash';
import { Subscription, combineLatest } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  take,
  tap,
  throttleTime,
} from 'rxjs/operators';
import { CapUtilService } from './services/caputil.service';
import { ChatService } from './services/eline/chat-service';
import { ElineHospitalService } from './services/eline/eline-hospital-service';
import { MakService } from './services/eline/mak-service';
import {
  CheckChannelParams,
  MyChatService,
} from './services/eline/mychat-service';
import { MetaService } from './services/meta.service';
import { NetworkService } from './services/network.service';
import { PushNotificationService } from './services/push-notification.service';
import {
  CheckToken,
  CheckTokenSuccess,
  DeleteAccount,
  Got401,
  Got403UserIsPending,
  Got404UserNotFound,
  LoadCacheAuth,
  LogOutSuccess,
  LoginSuccess,
  Logout,
  LogoutForDeleteAccount,
} from './state/auth/auth.actions';
import {
  InvokeChannelChanged,
  LoadChannels,
  UpdateChannels,
} from './state/channel/channel.actions';
import { InitLocalCacheElineHospitals } from './state/eline-hospital/eline-hospital.actions';
import {
  BindMak,
  InitLocalCacheMakDevices,
  ProcessMakDevice,
} from './state/mak-device/mak-device.actions';
import { MemberSignIn } from './state/member-action-log/member-action-log.actions';
import { ClearUser, RefreshElineUser } from './state/user/user.actions';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  @Select(UserState.eline_user) eline_user$;
  @Select(AuthState.elineUserAuthenticateChange) isElineAuthenticated$;
  @Select(AuthState.isMommyAuthenticated) isMommyAuthenticated$;

  currentPath = ''; // 紀錄目前的url path

  channel_subscription: Subscription;
  init_channels_subscription: Subscription; // init_channels 的 auth/user 訂閱（重複呼叫時先退訂, 避免疊加）
  max_firebase_sync_count = 9999; //避免無窮迴圈sync data from firebase,設定一個最大值

  // 加入預設團隊流程的 in-flight promise（single-flight, 防止併發重複觸發重複發送 welcome 訊息）
  private joinDefaultTeamPromise: Promise<void> | null = null;
  fbapp: fb.default.app.App;

  constructor(
    public navCtrl: NavController,
    private platform: Platform,

    private storage: StorageService,
    private store: Store,
    private router: Router,
    public toastCtrl: ToastController,
    private stateUtils: StateUtilsService,
    private util: UtilService,
    private caputil: CapUtilService,
    private chatsvc: ChatService,
    private actions$: Actions,
    private memberActionLogService: MemberActionLogService,
    private networkService: NetworkService,
    private pushService: PushNotificationService,
    private lineLogin: LineLogin,
    private zone: NgZone,
    private myChatService: MyChatService,
    private metaService: MetaService,
    private activatedRoute: ActivatedRoute,
    private makService: MakService,
    private elineHospitalService: ElineHospitalService
  ) {
    //this.showSplash(); // 目前都透過 capacitor.config.json 來設定 splash screen, 這部分先註解掉

    // 優先初始化 action 監聽器，確保在任何 action dispatch 之前就建立訂閱
    // 這樣可以正確捕捉：
    // 1. Capacitor deep link (appUrlOpen) 觸發的 action
    // 2. 網頁路由 (mak-device-redirect) 觸發的 action
    this.auth_actions_monitor();

    this.initializeApp();

    // 監控 router event, 來更新 meta tag
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
        tap((data) => {
          console.log('router.events: data', data);

          // 有在 route data 裡面設定 title/description 就更新, 沒有就不更新, 在 page 的程式裡面處理
          if (data.tile) {
            this.metaService.updateTitle(data.title);
          }
          if (data.description) {
            this.metaService.updateDescription(data.description);
          }
        })
      )
      .subscribe();
  }

  async showSplash() {
    console.log('showSplash');

    await this.platform.ready();

    const lottie = (window as any).lottie;

    // ios 會透過此方式來顯示 lottie splash, 故 config 內的 LottieAnimationLocation 設為 "" (空字串)
    // android 會透過 default config 的方式來顯示 lottie splash
    if (this.platform.is('ios') && lottie) {
      console.log('do showSplash');
      await lottie.splashscreen.hide();
      await lottie.splashscreen.show(
        'public/assets/lottie-splash.json',
        false,
        1000,
        1000
      );
    }
  }

  initializeApp() {
    if (!this.util._isServer) {
      App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        console.log('appUrlOpen', event);
        this.zone.run(async () => {
          // Example url: https://app.dev.mommycareyou.com/seo/pgc-post/1
          // slug = /#pgc-post/1

          // 先判斷是否為 deeplink url, 如果是才處理, 否則不處理
          if (event.url.startsWith(environment.APP_DEEPLINK_DOMAIN)) {
            const slug = event.url.split(environment.APP_DEEPLINK_DOMAIN).pop();
            if (slug) {
              console.log('slug', slug);

              // 處理 MAK Device 綁定
              if (slug.includes('/asafe/')) {
                const macAddress = slug.split('/asafe/')[1];
                if (macAddress) {
                  console.log('MAK Device mac_address', macAddress);
                  this.store.dispatch(new ProcessMakDevice(macAddress));
                  await this.navCtrl.navigateRoot('/tabs/home');
                  return;
                }
              }

              if (slug.startsWith('/register')) {
                console.log('appOpenUrl, link register');
                const urlObj = new URL(event.url);
                const queryParams = urlObj.searchParams;
                const team_id = queryParams.get('team_id');
                const referral_code = queryParams.get('referral_code');
                console.log('team_id', team_id);
                console.log('referral_code', referral_code);
                if (team_id) {
                  // take(1): deeplink 事件只處理一次, 避免殘留訂閱在後續認證狀態變動時重複觸發加入
                  this.isElineAuthenticated$.pipe(take(1)).subscribe(
                    async (isAuthenticated: boolean) => {
                      if (isAuthenticated) {
                        console.log(
                          'appOpenUrl, link register, already login, do link_hospital'
                        );
                        this.eline_user$
                          .pipe(
                            filter((eline_user) => !!eline_user),
                            take(1)
                          )
                          .subscribe(async (eline_user) => {
                            try {
                              await this.join_team_and_check_channel(
                                eline_user,
                                parseInt(team_id)
                              );
                            } catch (error) {
                              console.error('Error linking hospital:', error);
                            }
                          });
                      } else {
                        console.log(
                          'appOpenUrl, link register, not login, go to login page'
                        );
                        // 導至登入，帶入 team_id 參數，登入後再加入團隊
                        const queryString = referral_code
                          ? `?team_id=${team_id}&referral_code=${referral_code}`
                          : `?team_id=${team_id}`;
                        this.navCtrl.navigateForward(`/login${queryString}`);
                      }
                    }
                  );
                  // await this.navCtrl.navigateRoot('/tabs/home');
                  return;
                }
              }

              await this.navCtrl.navigateRoot('/tabs/home');
              this.navCtrl.navigateForward(slug.replace('/seo', ''));
            }
            // If no match, do nothing - let regular routing
            // logic take over
          } else {
            console.log('appUrlOpen, not deeplink url, open with inappbrower');
            this.caputil.iab_browser(event.url);
          }
        });
      });
    }

    this.platform.ready().then(async () => {
      if (this.util._isServer) {
        // 初始化app資料
        this.stateUtils.initAppState();
      } else {
        this.lineLogin.initialize({
          channel_id: environment.line_client_id,
        });
        console.log('lineLogin.initialize done');

        await SplashScreen.hide();
        console.log('platform ready: SplashScreen.hide()');

        // 初始化app資料
        this.stateUtils.initAppState();

        // 網頁版停用 孕關懷
        // 2025/2/27 之後, 網頁版也要啟用孕關懷
        // if (!environment.WEB_FLAG) {
        //   this.init_channels(); // 初始化 firebase channels
        // }

        this.init_channels(); // 初始化 firebase channels

        // auth_actions_monitor 已移至 constructor 中優先執行，避免遺失 action 事件
        // this.auth_actions_monitor();
        this.checkUserStatus();

        console.log('platform ready: networkService.init_service()');
        this.networkService.init_service();

        // 沒登入也要init firebasepush_plugin, 因為門號auth需要使用 push通知
        this.pushService.init_push_notification();

        // app啟動後, 預設將 video 聲音關閉
        localStorage.setItem('player_muted', 'true');

        // for mobile web, 推薦下載app
        this.util.check_app_download_recommend();
      }

      // this.statusBar.styleDefault();
      // //this.splashScreen.hide();
      // // 15sec一定關閉splashscreen
      // setTimeout(() => {
      //   SplashScreen.hide();
      // }, 15000);
      // // 紀錄 environment
      // let proj_env = await this.storage.get('proj_env');
      // if (!proj_env) {
      //   await this.storage.set('proj_env', environment.firebase.projectId);
      // }
      // this.init_network_monitor();
      // App.addListener('appUrlOpen', data => {
      //   console.log('App opened with URL:', data);
      //   this.zone.run(async () => {
      //     if (this.authService.isLogined) {
      //       console.log('已登入,ignore..');
      //     } else {
      //       const waitDynamicLink = await this.storage.get('WAIT_DYNAMIC_LINK');
      //       console.log('waitDynamicLink', waitDynamicLink);
      //       if (waitDynamicLink) {
      //         this.processSigninByEmailLink(data.url);
      //       }
      //     }
      //   });
      // });
      // this.authService.initAuthStateMonitor().then(resp => {
      //   if (resp) {
      //     // 已經login
      //     console.log('已經login');
      //     this.gotoInitPage();
      //     setTimeout(() => {
      //       SplashScreen.hide();
      //     }, 3000);
      //   } else {
      //     // 尚未login
      //     console.log('to welcome...');
      //     this.navCtrl.navigateRoot('/welcome', { animated: true, animationDirection: 'forward' });
      //     setTimeout(() => {
      //       SplashScreen.hide();
      //     }, 1000);
      //   }
      //   // 監控狀態變更
      //   this.authService.isAuthenticated.subscribe(val => {
      //     console.log('authService.isAuthenticated val', val);
      //     if (val) {
      //       console.log('auth狀態變成 authenticated.');
      //       this.firebaseUtil.setUserId(this.authService.userId);
      //     } else {
      //       console.log('auth狀態變成 not authenticated.');
      //       console.log('this.authService.loginProcess', this.authService.loginProcess);
      //       if (this.authService.loginProcess) {
      //         console.log('do nothing');
      //       } else {
      //         setTimeout(() => {
      //           console.log('to welcome...');
      //           this.navCtrl.navigateRoot('/welcome', { animated: true, animationDirection: 'forward' });
      //         }, 100);
      //       }
      //     }
      //   });
      // });
    });

    this.platform.resume.subscribe(async () => {
      console.log('Platform Resume...');
      this.checkUserStatus();

      console.log('platform resume: networkService.init_service()');
      this.networkService.init_service();
      this.pushService.init_push_notification();
    });
  }

  auth_actions_monitor() {
    // api 返回 401 狀態, 執行登出動作, 在提示用戶登入訊息
    this.actions$
      .pipe(ofActionDispatched(Got401))
      .pipe(throttleTime(10000))
      .subscribe(async () => {
        console.log('got Got401 action event!');
        this.store.dispatch([new Logout(), new ClearUser()]);
        const result = await this.util.showYconfirm(
          '帳號登入權杖已失效',
          '帳號登入權杖已失效，請重新登入',
          'mommy-alert-class'
        );
      });

    // api 返回 403 and UserIsPending 狀態, 執行登出動作, 在提示用戶訊息
    this.actions$
      .pipe(ofActionDispatched(Got403UserIsPending))
      .pipe(throttleTime(10000))
      .subscribe(async () => {
        console.log('got Got403UserIsPending action event!');
        this.store.dispatch([new Logout(), new ClearUser()]);
        const result = await this.util.showYconfirm(
          '帳號異常',
          '由於您的帳號異常，系統已自動登出',
          'mommy-alert-class'
        );
      });

    // api 返回 404 and user not found 狀態, 執行登出動作, 在提示用戶訊息
    this.actions$
      .pipe(ofActionDispatched(Got404UserNotFound))
      .pipe(throttleTime(10000))
      .subscribe(async () => {
        console.log('got Got404UserNotFound action event!');
        this.store.dispatch([new Logout(), new ClearUser()]);
        const result = await this.util.showYconfirm(
          '帳號不存在',
          '帳號不存在，請重新登入',
          'mommy-alert-class'
        );
      });

    // 用戶執行 delete account, 執行登出動作
    this.actions$
      .pipe(ofActionDispatched(DeleteAccount))
      .pipe(throttleTime(1000))
      .subscribe(async () => {
        console.log('got DeleteAccount action event!');
        this.store
          .dispatch([new LogoutForDeleteAccount(), new ClearUser()])
          .toPromise();
        console.log('LogoutForDeleteAccount, ClearUser done!');
      });

    // 用戶登入成功/CheckToken成功事件,
    // 執行 (1) 簽到動作, (2) 取得咪幣 (3) 取得EC購物車 (4) 設定孕關懷推播 (5) firebase analytics setUserId (6) 初始化本地 Mak Device cache
    this.actions$
      .pipe(ofActionDispatched(LoginSuccess, CheckTokenSuccess))
      .subscribe(async (value) => {
        console.log('got LoginSuccess action event!', value);
        this.store.dispatch(new MemberSignIn());
        // this.store.dispatch(new GetECCustomerCoinInfoFromServer());
        // this.store.dispatch(new GetECOrderCartFromServer());
        const elineUser = this.stateUtils.getElineUser();
        this.pushService.set_firebase_notify(elineUser);
        this.caputil.setUserId(elineUser.user_id);
        this.store.dispatch(new InitLocalCacheMakDevices());

        // 如果有 need_join_team_id (from referral code/QR code), 則加入團隊。
        // 掛在此處而非 init_channels 訂閱, 原因:
        // 1. init_channels 的 distinctUntilChanged 只在 user_id/family_channels 變動時放行,
        //    已登入狀態下掃 QR code 不會重新 emit, 加入團隊永遠不會被觸發。
        // 2. init_channels 內 joinTeamIfNeed 排在 join_default_team 之後, 前者若卡住會一併拖住。
        // CheckTokenSuccess 每次開啟 App 都會發送, 失敗時 localStorage 的 key 會保留, 下次自動重試。
        try {
          await this.joinTeamIfNeed(elineUser);
        } catch (error) {
          console.error('joinTeamIfNeed error, ignore', error);
        }
      });

    // 用戶登出成功事件,
    // 執行 (1) EC購物車/訂單..等state 清空 (2) 設定孕關懷推播->remove
    this.actions$
      .pipe(ofActionDispatched(LogOutSuccess))
      .subscribe(async (value) => {
        console.log('got LogOutSuccess action event!', value);
        // this.store.dispatch(new ClearECOrderState());
        await this.pushService.fcm_deleteInstance();
        // 刪除後需要重新 init, 以利收到活動推播
        this.pushService.init_push_notification();
      });

    // 用戶 孕關懷 channel 異動事件,
    this.actions$
      .pipe(ofActionDispatched(InvokeChannelChanged))
      .subscribe(async () => {
        console.log('got InvokeChannelChanged action event!');
        this.store
          .dispatch(new RefreshElineUser())
          .toPromise()
          .then(async (resp) => {
            console.log('重新init_channels');
            const elineUser = this.stateUtils.getElineUser();
            this.init_channels();
            this.pushService.set_firebase_notify(elineUser);
          });
      });

    // 用戶掃描 QR Code 包含 MAK Device MAC Address 事件
    this.actions$
      .pipe(ofActionDispatched(ProcessMakDevice))
      .pipe(throttleTime(3000)) // 防止重複觸發
      .subscribe(async (action: ProcessMakDevice) => {
        console.log('got ProcessMakDevice action event!', action.mac_address);
        await this.processMakDevice(action.mac_address);
      });
  }

  init_channels() {
    console.log('init_channels @app.component');

    // init_channels 會被重複呼叫（platform ready、InvokeChannelChanged）, 先退訂前一輪, 避免訂閱疊加
    if (this.init_channels_subscription) {
      this.init_channels_subscription.unsubscribe();
    }

    // 訂閱 auth_state + eline_user$（不嵌套, 避免訂閱疊加造成乘法觸發）:
    // 未登入或 user 空 → null; 以 user_id + family_channels 判斷實質變動, 過濾中間狀態的重複 emit
    this.init_channels_subscription = combineLatest([
      this.isElineAuthenticated$,
      this.eline_user$,
    ])
      .pipe(
        map(([isAuthenticated, eline_user]: any[]) =>
          isAuthenticated && eline_user ? eline_user : null
        ),
        distinctUntilChanged(
          (a: any, b: any) =>
            a?.user_id === b?.user_id &&
            JSON.stringify(a?.family_channels) ===
              JSON.stringify(b?.family_channels)
        )
      )
      .subscribe(async (eline_user: any) => {
        console.log('init_channels eline_user', eline_user);

        if (!eline_user) {
          // 尚未登入或 user 已清空, 退訂 firebase channel 資料
          console.log('eline_user is null, unsubscribe channels');
          if (this.channel_subscription) {
            this.channel_subscription.unsubscribe();
          }
          return;
        }

        // 已經登入, 先載入 local cache 的 channel 資料
        this.store
          .dispatch(new LoadChannels())
          .toPromise()
          .then(() => {
            console.log('load local cache channels success');
            // 計算 inquire badge count action => 在 tabs.ts 中訂閱計算
          });

        // for mommycare需求：預設加入全馨護的team
        try {
          await this.join_default_team(eline_user);
        } catch (error) {
          console.error('join_default_team error, ignore', error);
        }

        // 再載入 firebase 的 channel 資料
        if (this.channel_subscription) {
          this.channel_subscription.unsubscribe();
        }

        this.fbapp = fb.default.app(); // get default app

        this.channel_subscription = this.chatsvc
          .getChannelList_by_user_id_and_family_channels(
            this.fbapp,
            eline_user.user_id,
            eline_user.family_channels
          )
          .pipe(take(this.max_firebase_sync_count))
          .pipe(debounceTime(2000)) // 若有大量快速的 channel update, 應該可以有較好的體驗
          .subscribe((data_channels) => {
            console.log('data_channels==>', data_channels);

            data_channels.map((elem: any) => {
              elem.displayname = elem.tdn; //team_displayname
              elem.avatar = elem.ta; //team_avatar

              //判斷是否為家人的channel
              if (eline_user.family_without_self.includes(elem.uid)) {
                elem.family_flag = true;
              }

              // 如果unread_cnt>0,則嘗試check是否需要sync data from firebase to local

              // performance考量,暫時先不處理,再觀察看看影響情況
              // if(elem.unread_cnt > 0 ){
              //   this.chatsvc.sync_channel_data(this.fbapp,elem,this.user);
              // }

              // date format
              elem.dateformat = this.util.mydateformat(elem.lpat); //lastpostat
            });

            const channels = _.orderBy(data_channels, 'lpat', 'desc'); //lastpostat
            console.log(channels);
            this.store
              .dispatch(new UpdateChannels(channels))
              .toPromise()
              .then((resp) => {
                // this.store.dispatch(new InquireBadgeCaculatePatient());
              });
            // this.saveChannels(channels);

            /* 發送至tabs頁面去顯示badge count */
            // this.store.dispatch(new UpdateInquireUnReadCount(total_unread_count));
            // this.events.publish('total_unread_count', total_unread_count);
            // localStorage.setItem('total_unread_count', JSON.stringify(total_unread_count)); //切換頁面時不會有之前的event,所以要從loclstorage取得,未來可以考慮從ngstore來處理
            // this.util.saveLocalStorage();
          });
      });
  }

  // 檢查使用者目前的狀態(ex: 正常/停用/已刪帳號..,)
  async checkUserStatus() {
    console.log('checkUserStatus @app.component');

    await this.store.dispatch(new LoadCacheAuth()).toPromise();
    const isMommyAuthenticated = this.stateUtils.isMommyAuthenticated();

    if (isMommyAuthenticated) {
      try {
        // 初始化本地快取的 MAK Device 列表
        this.store.dispatch(new InitLocalCacheMakDevices());

        await this.store.dispatch(new CheckToken()).toPromise();
        console.log('checkUserStatus success');

        // 觸發 CheckToken 成功事件 -> 簽到,取得EC購物車...etc
        this.store.dispatch(new CheckTokenSuccess()).toPromise();
      } catch (error) {
        console.error('CheckToken error', error);
      }
    } else {
      console.log('not isMommyAuthenticated, skip checkUserStatus');
    }
  }

  async init_network_monitor() {
    await Network.removeAllListeners();
    Network.addListener('networkStatusChange', (status) => {
      console.log('Network status changed', status);
      if (status.connected) {
        console.log('network connected!');
        this.util.showToast('你的網路已恢復');
      } else {
        console.log('network disconnected!');
        this.util.showToast('你的網路是離線狀態');
      }
    });
  }

  // for mommycare需求：預設加入全馨護的team
  // single-flight: 併發呼叫共用同一 in-flight promise, 防止 check-then-act race 造成重複加入/重複發送 welcome
  async join_default_team(eline_user) {
    if (this.joinDefaultTeamPromise) {
      console.log('join_default_team in-flight, 共用進行中結果');
      return this.joinDefaultTeamPromise;
    }

    this.joinDefaultTeamPromise = (async () => {
      console.warn('join_default_team');

      const join_default_team_flag = await this.storage.get(
        'join_default_team_flag'
      );

      if (!join_default_team_flag) {
        console.log('join_default_team_flag not set, do join_default_team');
        await this.join_team_and_check_channel(
          eline_user,
          environment.DEFAULT_TEAM_ID
        );
        await this.storage.set('join_default_team_flag', true);
      }
    })();

    try {
      return await this.joinDefaultTeamPromise;
    } catch (error) {
      // 失敗時釋放 in-flight, 讓下次狀態變動觸發時可重試（成功則保留, flag 已寫入行為等價）
      this.joinDefaultTeamPromise = null;
      throw error;
    }
  }

  async join_team_and_check_channel(eline_user, team_id: number) {
    try {
      await this.elineHospitalService.link_hospital({ team_id });
      console.log('link_hospital success, team_id', team_id);

      const hospital = this.stateUtils.getElineHospital(team_id);
      const params: CheckChannelParams = {};
      params.selectedHospital = hospital;
      params.user = eline_user;
      params.createChannelIfNotExist = true;
      params.sendWelcomeMessage = true;

      const channel = await this.myChatService.checkChannelInstance(params);
      console.log('channel_check_result', channel);
    } catch (error) {
      console.error('join_team_and_check_channel error', error);
      // rethrow
      throw error;
    }
  }

  // 如果有 need_join_team_id (from referral code/QR code), 則加入團隊
  async joinTeamIfNeed(eline_user) {
    console.log('joinTeamIfNeed @app.component');

    // SSR 沒有 localStorage
    if (this.util._isServer) {
      return;
    }

    const need_join_team_id = localStorage.getItem('need_join_team_id')!;
    if (!need_join_team_id) {
      return;
    }

    if (!eline_user) {
      // user 尚未就緒, 保留 key, 待下次登入/開啟 App 時重試
      console.warn('joinTeamIfNeed: eline_user not ready, skip');
      return;
    }

    const team_id = parseInt(need_join_team_id);
    if (isNaN(team_id)) {
      console.warn('joinTeamIfNeed: invalid team_id', need_join_team_id);
      localStorage.removeItem('need_join_team_id');
      return;
    }

    // join_team_and_check_channel 會用 getElineHospital(team_id) 取團隊資料,
    // 若本地 cache 尚未載入會取不到, 這裡先確保醫療團隊資料已就緒
    if (!this.stateUtils.getElineHospital(team_id)) {
      console.log('joinTeamIfNeed: eline hospital cache miss, reload');
      await this.store
        .dispatch(new InitLocalCacheElineHospitals())
        .toPromise();
    }

    // 失敗時 join_team_and_check_channel 會 rethrow, 不會執行到 removeItem,
    // key 保留給下次 CheckTokenSuccess 重試
    await this.join_team_and_check_channel(eline_user, team_id);
    localStorage.removeItem('need_join_team_id');
    console.log('joinTeamIfNeed done, team_id', team_id);
  }

  // 處理 MAK Device MAC Address 綁定
  async processMakDevice(mac_address: string) {
    console.log('processMakDevice @app.component', mac_address);

    await this.checkUserStatus();

    // 檢查是否登入
    const isMommyAuthenticated = this.stateUtils.isMommyAuthenticated();

    if (!isMommyAuthenticated) {
      // 未登入：儲存參數，等登入後處理
      localStorage.setItem('pending_mak_device_mac', mac_address);
      await this.util.showLoginPopupModal(
        `已偵測到脈安扣QRCode，您需要先登入後才能繼續使用脈安扣！`
      );
      this.navCtrl.navigateForward(['/login']);
      return;
    }

    // 已登入：執行業務邏輯
    // Step 1: 呼叫 checkStatus 取得此脈安扣的資訊
    let checkResult: any;
    try {
      checkResult = await this.makService.checkMakStatus(mac_address);
      console.log('checkMakStatus result', checkResult);
    } catch (error) {
      console.error('checkMakStatus error', error);
      // 如果查無此裝置 (api_error: true), 提示錯誤訊息
      if (error?.api_error) {
        await this.util.showYconfirm(
          '脈安扣裝置錯誤',
          '此脈安扣裝置不存在或尚未啟用，請確認後再試',
          'mommy-alert-class'
        );
      } else {
        this.util.showToast('脈安扣裝置綁定失敗，請稍後再試');
      }
      return;
    }

    try {
      // Step 2: 檢查裝置狀態
      const { team_id, customer_id, user_id } = checkResult.data;
      const elineUser = this.stateUtils.getElineUser();
      const currentUserId = elineUser?.user_id;

      // Step 2-1: 如果 team_id is null (尚未配發給團隊)
      if (!team_id) {
        await this.util.showYconfirm(
          '脈安扣裝置錯誤',
          '此脈安扣裝置尚未啟用',
          'mommy-alert-class'
        );
        return;
      }

      // Step 2-2: 如果已綁定 (customer_id 不為 null)
      if (customer_id) {
        if (user_id === currentUserId) {
          // 已綁定且是自己的裝置
          await this.util.showYconfirm(
            '脈安扣裝置',
            '歡迎使用您的脈安扣裝置',
            'mommy-alert-class'
          );
          // 導航到我的頁
          this.navCtrl.navigateRoot('/tabs/member');
        } else {
          // 已綁定但不是自己的裝置
          await this.util.showYconfirm(
            '脈安扣裝置錯誤',
            '此脈安扣裝置已被其他使用者綁定，請聯繫醫療人員協助處理',
            'mommy-alert-class'
          );
        }
        return;
      }

      // Step 2A: 尚未綁定 (customer_id is null), 進行綁定動作
      // 確保 ElineHospitalState 已載入資料
      await this.store.dispatch(new InitLocalCacheElineHospitals()).toPromise();
      const hospital = this.stateUtils.getElineHospital(team_id);
      const teamName = hospital?.team_name || '醫療團隊';

      try {
        // 提示用戶確認視窗
        await this.util.showYNconfirm(
          '綁定脈安扣裝置',
          `確定要加入醫療團隊「${teamName}」並綁定此脈安扣裝置嗎？`,
          'mommy-alert-class'
        );

        // 用戶確認後，執行綁定動作
        // (1) 先呼叫 link_hospital service 加入團隊
        const linkParams = { team_id };
        await this.elineHospitalService.link_hospital(linkParams);
        console.log('link_hospital success');

        // (2) 呼叫 dispatch BindMak action 進行綁定動作
        await this.store
          .dispatch(
            new BindMak(
              team_id.toString(),
              currentUserId.toString(),
              mac_address
            )
          )
          .toPromise();
        console.log('BindMak success');

        // 綁定成功訊息
        await this.util.showYconfirm(
          '綁定成功',
          '脈安扣裝置綁定成功',
          'mommy-alert-class'
        );

        // 重新整理 MAK 裝置清單
        this.store.dispatch(new InitLocalCacheMakDevices());

        // 導航到會員頁
        this.navCtrl.navigateRoot('/tabs/member');
      } catch (confirmError) {
        // 用戶取消確認視窗，不做任何動作
        console.log('用戶取消綁定', confirmError);
      }
    } catch (error) {
      console.error('processMakDevice error', error);
      this.util.showToast('脈安扣裝置綁定失敗，請稍後再試');
    }
  }
}
